import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FEATURED_GUESTS } from "@/data/site";
import useApiCollection from "@/lib/useApiCollection";
import { imageUrl } from "@/lib/api";

export default function GuestCarousel() {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  // Pull from admin-managed performers; fall back to the legacy static list if empty.
  const { data: adminPerformers } = useApiCollection("/api/performers");
  const performers =
    adminPerformers && adminPerformers.length > 0
      ? adminPerformers.map((p) => ({
          img: imageUrl(p.image_url),
          name: p.name,
          type: p.type,
        }))
      : FEATURED_GUESTS;

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const slides = Array.from(scroller.children);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const idx = slides.indexOf(entry.target);
            if (idx !== -1) setActive(idx);
          }
        });
      },
      { root: scroller, threshold: [0.6, 0.8, 1] },
    );

    slides.forEach((slide) => io.observe(slide));
    return () => io.disconnect();
  }, [performers.length]);

  const scrollTo = (idx) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const slide = scroller.children[idx];
    if (slide) {
      scroller.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    }
  };

  return (
    <div data-testid="guest-carousel" className="relative">
      <div
        ref={scrollerRef}
        className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory scroll-smooth gap-4 sm:gap-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pb-2"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {performers.map((g, i) => (
          <motion.figure
            key={(g.name || "") + i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            data-testid={`featured-guest-${i}`}
            className="snap-center shrink-0 w-[88%] sm:w-[72%] md:w-[58%] lg:w-[46%]"
          >
            <div className="relative overflow-hidden rounded-xl aspect-square sm:aspect-[5/6] bg-tnt-surface2 border border-tnt-border shadow-sm">
              <img
                src={g.img}
                alt={`${g.name} at Taps & Tunes`}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            <figcaption className="mt-4 sm:mt-5">
              {g.type && (
                <p
                  data-testid={`featured-guest-type-${i}`}
                  className="font-display text-tnt-gold text-[11px] sm:text-xs tracking-[0.32em] uppercase mb-1.5"
                >
                  {g.type}
                </p>
              )}
              <h3
                data-testid={`featured-guest-name-${i}`}
                className="font-display text-tnt-ink text-2xl sm:text-3xl md:text-4xl tracking-tight leading-[1.05]"
              >
                {g.name}
              </h3>
              <div className="mt-2.5 h-px w-10 bg-tnt-gold/70" />
              <p className="mt-2 text-tnt-ink/55 text-xs sm:text-sm tracking-wide">
                Live at Taps &amp; Tunes
              </p>
            </figcaption>
          </motion.figure>
        ))}
      </div>

      {/* Dots */}
      <div
        data-testid="guest-carousel-dots"
        className="mt-6 sm:mt-8 flex items-center justify-center gap-2.5"
      >
        {performers.map((_, i) => (
          <button
            key={i}
            type="button"
            data-testid={`guest-carousel-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => scrollTo(i)}
            className={`transition-all duration-300 rounded-full ${
              active === i
                ? "w-7 h-2 bg-tnt-gold"
                : "w-2 h-2 bg-tnt-ink/20 hover:bg-tnt-ink/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
